import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { defaultRouteForRole } from "@/components/ProtectedRoute";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CarProfile } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [require2FA, setRequire2FA] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [rEmail, setREmail] = useState("");
  const [rName, setRName] = useState("");
  const [rPassword, setRPassword] = useState("");
  const [rPhone, setRPhone] = useState("");
  const [rPlate, setRPlate] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password, totp || null, rememberMe);
      if (result?.requires_2fa) {
        setRequire2FA(true);
        toast.info("2FA kodu gerekli (Authenticator uygulamasından)");
        return;
      }
      toast.success(`Hoş geldin, ${result.name}`);
      nav(loc.state?.from?.pathname || defaultRouteForRole(result.role), { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Giriş başarısız");
    } finally { setLoading(false); }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register({
        email: rEmail, name: rName, password: rPassword,
        phone: rPhone || null, plate: rPlate || null, role: "customer",
      });
      toast.success("Kayıt tamam. Hoş geldin!");
      nav(defaultRouteForRole(user.role), { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kayıt başarısız");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div
        className="relative hidden md:block bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(0,0,0,0.75), rgba(0,0,0,0.4)), url('https://images.unsplash.com/photo-1548343361-02248be15911?w=1600')",
        }}
      >
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary flex items-center justify-center rounded-sm flex-shrink-0">
              <CarProfile size={28} weight="fill" className="text-black" />
            </div>
            <div>
              <div className="text-xl md:text-2xl font-bold tracking-tight leading-tight">
                DEBBOY OTOPARK
              </div>
              <div className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/60 leading-tight">
                Plaka Tanıma ve Yönetim Sistemi
              </div>
            </div>
          </div>
          <div>
            <h1 className="text-5xl font-bold tracking-tight mb-4">
              Hoş Geldiniz
            </h1>
            <p className="text-white/70 max-w-md text-base leading-relaxed">
              Akıllı ve bulut tabanlı plaka tanıma yönetim panelinize erişmek için
              lütfen bilgilerinizi girin. Otoparkınızın güvenliği ve takibi tek bir
              tık uzağınızda.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md rounded-sm border-border">
          <CardContent className="p-8">
            <div className="mb-6">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">
                Yönetim Paneli
              </div>
              <h2 className="text-3xl font-bold tracking-tight">
                Debboy Otopark Plaka Tanıma ve Yönetim Sistemi
              </h2>
            </div>

            <Tabs defaultValue="login">
              <TabsList className="grid grid-cols-2 rounded-sm">
                <TabsTrigger value="login" data-testid="tab-login" className="rounded-sm">Giriş</TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register" className="rounded-sm">Müşteri Kaydı</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="mt-6">
                <form onSubmit={onSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      data-testid="login-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1.5 rounded-sm"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Şifre</Label>
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1.5 rounded-sm"
                      required
                    />
                  </div>
                  {require2FA && (
                    <div>
                      <Label htmlFor="totp">2FA Kodu (6 haneli)</Label>
                      <Input
                        id="totp"
                        data-testid="login-totp-input"
                        value={totp}
                        onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        className="mt-1.5 rounded-sm font-mono text-lg tracking-widest text-center"
                        placeholder="123456"
                        autoFocus
                      />
                      <div className="text-xs text-muted-foreground mt-1">
                        Google Authenticator / Microsoft Authenticator gibi bir uygulamadan
                      </div>
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="login-remember-label">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 accent-primary cursor-pointer"
                      data-testid="login-remember-checkbox"
                    />
                    <span>Beni Hatırla</span>
                    <span className="text-[10px] text-muted-foreground uppercase tracking-widest ml-auto">
                      Operatör · 30 gün
                    </span>
                  </label>
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit-btn"
                    className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-semibold uppercase tracking-widest"
                  >
                    {loading ? "Giriş yapılıyor..." : (require2FA ? "Doğrula ve giriş" : "Giriş yap")}
                  </Button>
                  <div className="text-center space-y-1">
                    <button
                      type="button"
                      onClick={() => nav("/forgot-password")}
                      data-testid="login-forgot-link"
                      className="block w-full text-xs text-muted-foreground hover:text-primary underline underline-offset-4 tracking-wide"
                    >
                      Şifremi unuttum
                    </button>
                    <button
                      type="button"
                      onClick={() => nav("/debt")}
                      data-testid="login-debt-link"
                      className="block w-full text-xs text-muted-foreground hover:text-primary underline underline-offset-4 tracking-wide"
                    >
                      Otopark borç sorgulama →
                    </button>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="register" className="mt-6">
                <form onSubmit={onRegister} className="space-y-4">
                  <div>
                    <Label>Ad Soyad</Label>
                    <Input data-testid="reg-name" value={rName} onChange={(e) => setRName(e.target.value)} className="mt-1.5 rounded-sm" required />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input data-testid="reg-email" type="email" value={rEmail} onChange={(e) => setREmail(e.target.value)} className="mt-1.5 rounded-sm" required />
                  </div>
                  <div>
                    <Label>Şifre</Label>
                    <Input data-testid="reg-password" type="password" value={rPassword} onChange={(e) => setRPassword(e.target.value)} className="mt-1.5 rounded-sm" required minLength={10} />
                    <div className="text-[10px] text-muted-foreground mt-1">
                      En az 10 karakter · harf + rakam içermeli
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Telefon</Label>
                      <Input data-testid="reg-phone" value={rPhone} onChange={(e) => setRPhone(e.target.value)} className="mt-1.5 rounded-sm" />
                    </div>
                    <div>
                      <Label>Plaka (opsiyonel)</Label>
                      <Input data-testid="reg-plate" value={rPlate} onChange={(e) => setRPlate(e.target.value.toUpperCase())} className="mt-1.5 rounded-sm font-plate uppercase" />
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Otopark yönetiminin onayı sonrası bağlanacak
                      </div>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="reg-submit"
                    className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-semibold uppercase tracking-widest"
                  >
                    {loading ? "Kaydediliyor..." : "Kayıt ol"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
